// #334: the calendar and the map were future-only. `/api/v1/events` has always
// accepted `from` / `to`, and an anonymous caller may list past events — so
// "show me the past" needs no new API surface, just a control that writes a
// sensible pair of dates into the filters both pages already send.
//
// Presets are expressed as whole years rather than a "past events" switch on
// purpose. The API sorts ascending by `startsAtUtc` and has no sort parameter,
// so an open-ended "everything before now" range would page from the oldest
// event forward — the least interesting end first. A year is a range a reader
// already has a mental model of, reads chronologically, and is what was asked
// for ("All of 2026").

// Dates are handled as plain `YYYY-MM-DD` strings, matching what the From / To
// `<input type="date">` fields hold and what `paramsForApi` expects.
const pad = (n) => String(n).padStart(2, '0')

export function isoDate(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export const UPCOMING = 'upcoming'
export const CUSTOM = 'custom'

// The options offered by the control, in display order. `now` is injected so
// tests can pin a date and so the labels cannot drift from the range they set.
//
// #338: only the CURRENT year is offered. #334 also listed the previous year,
// which had no events at all and so was a button whose only outcome was an
// empty page. Other years did not stop existing — /2025 renders perfectly well
// when typed or linked to, and `optionsFor` below makes the picker show the
// year you are actually on even when it is not the current one.
export function rangeOptions(now = new Date()) {
  const year = now.getFullYear()
  return [
    { id: UPCOMING, label: 'Upcoming', from: '', to: '' },
    yearOption(year),
  ]
}

export function yearOption(year) {
  return {
    id: `year-${year}`,
    label: `All of ${year}`,
    from: `${year}-01-01`,
    to: `${year}-12-31`,
  }
}

// What the picker actually renders. Normally the standard options; on a year
// route that is not the current year, that year is appended so the control can
// show it as pressed rather than falling back to `Custom` — the reader is on a
// perfectly ordinary year view, not an exotic hand-typed range.
export function optionsFor(value = {}, now = new Date()) {
  const options = rangeOptions(now)
  const match = options.find((o) => o.from === (value.from || '') && o.to === (value.to || ''))
  if (match) return options
  const onAYear = /^(\d{4})-01-01$/.exec(value.from || '')
  if (onAYear && value.to === `${onAYear[1]}-12-31`) {
    return [...options, yearOption(Number(onAYear[1]))]
  }
  return options
}

// Which preset the current filter values correspond to. Typing directly into
// From / To is legitimate, so anything that matches no preset resolves to
// CUSTOM rather than snapping the control back to a range the user did not
// choose — the control would otherwise silently disagree with the fields.
export function activeRangeId(value = {}, now = new Date()) {
  const from = value.from || ''
  const to = value.to || ''
  const match = optionsFor(value, now).find((o) => o.from === from && o.to === to)
  return match ? match.id : CUSTOM
}

// The from/to patch for a preset. An unknown id clears the range, which is
// also what `Upcoming` does — the API's own default is "from now on".
export function rangePatch(id, now = new Date()) {
  const fromYearId = /^year-(\d{4})$/.exec(id || '')
  const option = fromYearId
    ? yearOption(Number(fromYearId[1]))
    : rangeOptions(now).find((o) => o.id === id)
  return { from: option?.from || '', to: option?.to || '' }
}

// True when the selected range can contain events that have already happened.
// Drives the home-page strapline (which must stop saying "upcoming") and the
// map legend (which only earns its space once past markers are on screen).
export function includesPast(value = {}, now = new Date()) {
  if (!value.from) return false
  // `from` is a date, `now` an instant: compare on the date, so an event
  // earlier today still counts as past-inclusive.
  return value.from <= isoDate(now)
}

// Whether a single event has already started. One definition, used by the
// card list, the map markers and their legend, so the three cannot disagree
// about what "past" means.
export function isPastEvent(event, now = new Date()) {
  const iso = event?.startsAtUtc
  if (!iso) return false
  const t = new Date(iso).getTime()
  return Number.isFinite(t) && t < now.getTime()
}
